import Badge from '@/components/badges/Badge'
import DualIcon from '@/components/tokens/DualIcon'
import { useGetNetworkIcon, useGetWalletIcon } from '@/components/tokens/WalletIcon'
import { Button } from '@/components/ui/button'
import RenderIf from '@/components/ui/render-if'
import { Text } from '@/components/ui/text'
import { useNativeToken } from '@/context'
import { useTokenBalance } from '@/hooks'
import { CommonArrowDropDown, CommonCaptive, CommonCopy, CommonLogoutNone } from '@/icons'
import {
  formatTokenAmount,
  getBtcWalletAddressTypeLabel,
  isBtcChain,
  toTxBlockExplorer,
  truncateAddress,
} from '@/utils'
import { WalletContextType } from '@/wallets'

import { useTranslation } from 'react-i18next'

interface WalletItemProps {
  wallet: WalletContextType
  address: string
  onDisconnect: (e: React.MouseEvent) => void
  onCopy: (address: string) => void
  setOpenSelectWallet: (v: WalletContextType) => void
}

const WalletItem: React.FC<WalletItemProps> = ({ wallet, onDisconnect, onCopy, setOpenSelectWallet, address }) => {
  const { t } = useTranslation()
  const token = useNativeToken(wallet?.network)
  const { data: balance } = useTokenBalance({ token, walletAddress: address })
  const getWalletIcon = useGetWalletIcon()

  // Show BTC wallet type badge and selector if multiple BTC addresses
  const showBtcWalletType =
    isBtcChain(wallet?.network ?? '') && address && (wallet?.wallet?.getAddresses?.()?.length || 0) > 1
  const getNetworkLogo = useGetNetworkIcon()
  return (
    <div className='flex items-center justify-between py-3 px-4 gap-x-7'>
      <div className='flex-1 flex items-center md:gap-x-1 gap-x-2'>
        <DualIcon
          primaryClassName='size-7 md:size-10 rounded-lg'
          secondaryClassName='size-3 md:size-4 border border-[1.5px] border-black-100 rounded outline-dark-3 -right-1.5 -bottom-px'
          primaryIcon={getNetworkLogo(wallet.networkInfo?.networkId)}
          secondaryIcon={getWalletIcon(wallet.wallet)}
        />
        <div className='flex flex-col gap-0.5'>
          <Text>
            {formatTokenAmount(balance || '0', token?.tokenSymbol || '')} {wallet.networkInfo?.networkSymbol || ''}
          </Text>
          <Text size={12} color='white-48' className='flex items-center gap-1.5'>
            {truncateAddress(address || '')}
            <RenderIf condition={showBtcWalletType}>
              <Badge
                className='rounded-sm px-2 text-size-12 bg-white-4 text-white'
                text={getBtcWalletAddressTypeLabel(address)}
              />
            </RenderIf>
          </Text>
          <RenderIf condition={showBtcWalletType}>
            <Text
              className='flex items-center cursor-pointer'
              size={12}
              color='white-48'
              onClick={() => setOpenSelectWallet(wallet)}
            >
              {t('button.SET_DEFAULT_ADDRESS')}
              <CommonArrowDropDown className='size-4 rotate-[-90deg]' />
            </Text>
          </RenderIf>
        </div>
      </div>
      <div className='flex items-center gap-x-2'>
        <Button className='w-8' variant='icon' size='sm' aria-label={t('button.COPY_ADDRESS')}>
          <CommonCopy onClick={() => onCopy(address || '')} className='size-6 text-white-100' />
        </Button>
        <Button className='w-8' variant='icon' size='sm' aria-label={t('button.VIEW_ON_EXPLORER')}>
          <CommonCaptive
            className='size-6 text-white-100'
            onClick={() => {
              window.open(toTxBlockExplorer(wallet.networkInfo?.networkId ?? '', address ?? '', 'address'), '_blank')
            }}
          />
        </Button>
        <Button className='w-8' variant='icon' size='sm' aria-label={t('button.DISCONNECT_WALLET')}>
          <CommonLogoutNone onClick={onDisconnect} className='size-6 text-white-100' />
        </Button>
      </div>
    </div>
  )
}

export default WalletItem
